import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import { PrimaryButton } from '../../components/Button'
import PageHeader from '../../components/PageHeader'
import AutomationModal from './AutomationModal'
import Radom, { Automation, IAutoSwapOnPaymentSettings, WithdrawalAccount } from '../../state/Radom'
import ConfirmButtonModal from '../../components/ConfirmButtonModal'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import Dots from '../../icons/Dots'
import { cutString, shortAddress, successToast } from '../../util/Util'
import { startCase, uniq, uniqBy } from 'lodash'
import { Container } from '../../components/Animations'
import {
  DashboardPage,
  EmptyState,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue,
  Surface,
  SurfaceHeader,
  SurfaceTitle
} from '../../components/DashboardPrimitives'
import LoadingBar from '../../components/LoadingBar'
import { ManagedPaymentMethod, getMethod } from '../../util/Managed'
import { formatWithdrawalAccount } from '../../components/Shared'
import { formatDistanceToNow } from 'date-fns'
import { WINDOW_SIZE } from '../../util/Constants'
import WithdrawalTransactionIcon from '../../icons/WithdrawalTransactionIcon'
import SwapTransactionIcon from '../../icons/SwapTransactionIcon'
import Gate from '../../components/Gate'
import { usePermissions } from '../../util/permissions'

interface FlowDestination {
  label: string
  sub?: string
  logo?: string
}

const getAutomationAssetMethods = (automation: Automation): ManagedPaymentMethod[] => {
  const assets = automation.automationDetails.Withdrawal?.assets ?? []
  return uniqBy(
    assets.map(asset => getMethod(asset.network, asset.token)),
    method => method.ticker
  )
}

const getAutomationAssetsLabel = (automation: Automation): string => {
  const assets = automation.automationDetails.Withdrawal?.assets ?? []
  if (assets.length <= 0) {
    return 'No assets selected'
  }

  return uniq(assets.map(asset => getMethod(asset.network, asset.token).ticker)).join(', ')
}

const getAutomationTriggerLabel = (automation: Automation): string => {
  const triggers = automation.automationDetails.Withdrawal?.triggers ?? []
  const minimumAmount = triggers.find(t => t.minimumAmount)?.minimumAmount
  const timeInterval = triggers.find(t => t.timeInterval)?.timeInterval
  const withdrawalAmount = triggers.find(t => t.withdrawalAmount)?.withdrawalAmount

  if (withdrawalAmount && timeInterval) {
    return `${withdrawalAmount.amount} ${withdrawalAmount.currency} · ${startCase(timeInterval.interval)}`
  }

  if (minimumAmount) {
    return `Balance above ${minimumAmount.amount} ${minimumAmount.currency}`
  }

  if (timeInterval) {
    return startCase(timeInterval.interval)
  }

  return 'No trigger configured'
}

const resolveDestination = (
  automation: Automation,
  accounts: WithdrawalAccount[]
): FlowDestination => {
  const id = automation.automationDetails.Withdrawal?.withdrawalAccountId
  if (!id) {
    return { label: 'No destination' }
  }

  const account = accounts.find(a => a.id === id)
  if (!account) {
    return { label: cutString(id, 8) }
  }

  if (account.accountDetails.Crypto) {
    return {
      label: account.name,
      sub: shortAddress(account.accountDetails.Crypto.address),
      logo: getMethod(account.accountDetails.Crypto.network).logo
    }
  }

  if (account.accountDetails.Fiat) {
    return {
      label: account.name,
      sub: formatWithdrawalAccount(account.accountDetails) as string
    }
  }

  return { label: account.name }
}

const FlowArrow = (): ReactElement => (
  <svg className='flow-arrow' width='15' height='15' viewBox='0 0 24 24' aria-hidden='true'>
    <path
      d='M5 12h14m0 0-5.5-5.5M19 12l-5.5 5.5'
      fill='none'
      stroke='#c2cad7'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round' />
  </svg>
)

const RuleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`

const RuleCard = styled.div`
  background: var(--surface, #ffffff);
  border: 1px solid var(--surface-pressed, #edf1f7);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  padding: 15px 16px;
  transition: border-color 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    border-color: #e0e6f0;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
  }
`

const RuleTopRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-width: 0;
`

const RuleIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
`

const RuleIcon = styled.div`
  align-items: center;
  background: #2A63FF;
  border-radius: 8px;
  color: #ffffff;
  display: flex;
  flex: 0 0 26px;
  height: 26px;
  justify-content: center;
  width: 26px;
`

const RuleNameStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const RuleName = styled.span`
  color: var(--ink, #20242d);
  font-size: 14.5px;
  font-weight: 500;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RuleSubMeta = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RuleTopMeta = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`

const RuleFlow = styled.div`
  align-items: stretch;
  display: flex;
  gap: 8px;
  min-width: 0;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    flex-direction: column;

    .flow-arrow {
      transform: rotate(90deg);
    }
  }
`

const FlowSegment = styled.div`
  background: var(--surface-subtle, #fafbfd);
  border: 1px solid var(--surface-pressed, #eef2f7);
  border-radius: 11px;
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 9px 12px;
`

const FlowArrowCell = styled.div`
  align-items: center;
  color: #c2cad7;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
`

const FlowLabel = styled.span`
  color: #8b93a3;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  line-height: 1;
  text-transform: uppercase;
`

const FlowValue = styled.div`
  align-items: center;
  color: var(--ink, #20242d);
  display: flex;
  font-size: 13px;
  font-weight: 500;
  gap: 7px;
  line-height: 1.3;
  min-width: 0;
`

const FlowValueText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FlowSub = styled.span`
  color: #9aa1b1;
  font-size: 11.5px;
  font-weight: 400;
`

const AssetLogoStack = styled.div`
  align-items: center;
  display: flex;

  img {
    border: 1.5px solid #fff;
    border-radius: 50%;
    height: 19px;
    width: 19px;
  }

  img + img {
    margin-left: -7px;
  }
`

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
`

const AssetIcons = ({ methods }: { methods: ManagedPaymentMethod[] }): ReactElement | null => {
  const withLogos = methods.filter(m => m.logo).slice(0, 4)
  if (withLogos.length === 0) {
    return null
  }

  return <AssetLogoStack>
    {withLogos.map(method => (
      <img key={method.ticker} src={method.logo} alt={method.ticker} />
    ))}
  </AssetLogoStack>
}

const RuleSegment = ({
  label,
  children
}: { label: string, children: React.ReactNode }): ReactElement =>
  <FlowSegment>
    <FlowLabel>{label}</FlowLabel>
    <FlowValue>{children}</FlowValue>
  </FlowSegment>

const RuleFlowRow = ({ segments }: { segments: ReactElement[] }): ReactElement =>
  <RuleFlow>
    {segments.map((segment, i) => <React.Fragment key={i}>
      {i > 0 && <FlowArrowCell><FlowArrow /></FlowArrowCell>}
      {segment}
    </React.Fragment>)}
  </RuleFlow>

const ActionsMenu = ({
  title,
  description,
  action,
  actionLabel,
  onConfirm
}: {
  title: string
  description: string
  action: string
  actionLabel: string
  onConfirm: () => Promise<void>
}): ReactElement =>
  <ActionsCell onClick={e => e.stopPropagation()}>
    <Dropdown
      noChevron
      iconTrigger
      preferredAlignment='right'
      selectedContent={<Dots width={15} />}
      dropdownContent={
        <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
          <ConfirmButtonModal
            title={title}
            description={description}
            action={action}
            onConfirm={onConfirm}
            ActionComponent={(props: React.HTMLAttributes<HTMLDivElement>) =>
              <DropdownItem {...props}>{actionLabel}</DropdownItem>}
          />
        </div>
      }
    />
  </ActionsCell>

const AutomationView = observer((): ReactElement => {
  const [isOpen, setIsOpen] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [automations, setAutomations] = useState<Automation[]>([])
  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])
  const [autoSwapSettings, setAutoSwapSettings] = useState<IAutoSwapOnPaymentSettings | null>(null)
  const { has, isLoaded: permissionsLoaded } = usePermissions()
  const canReadWithdrawalAccounts = has('ReadWithdrawalAccount')
  const canWriteAutomation = has('WriteAutomation')

  const listAutomations = (): void => {
    setIsLoading(true)
    setAutomations([])
    setAutoSwapSettings(null)

    Promise.all([
      Radom.listAutomations(),
      Radom.getAutoSwapOnPayment().catch((err) => {
        console.error('Failed to load auto-swap settings', err)
        return null
      }),
      canReadWithdrawalAccounts
        ? Radom.listWithdrawalAccounts().catch((err) => {
          console.error('Failed to load withdrawal accounts', err)
          return [] as WithdrawalAccount[]
        })
        : Promise.resolve([] as WithdrawalAccount[])
    ])
      .then(([a, swapSettings, accounts]) => {
        setAutomations(a)
        setAutoSwapSettings(swapSettings)
        setWithdrawalAccounts(accounts)
      })
      .catch(err => {
        console.error('Failed to load automations', err)
        setAutomations([])
        setAutoSwapSettings(null)
        setWithdrawalAccounts([])
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    if (!permissionsLoaded) {
      return
    }
    listAutomations()
  }, [permissionsLoaded])

  const autoSwapEnabled = autoSwapSettings?.enabled === true
  const withdrawalAutomationCount = automations.filter(a => a.automationType === 'withdrawal').length
  const totalRules = automations.length + (autoSwapEnabled ? 1 : 0)
  const destinationAccountCount = uniq(automations
    .map(a => a.automationDetails.Withdrawal?.withdrawalAccountId)
    .filter(Boolean)).length
  const watchedAssetCount = uniq(automations.flatMap(a =>
    (a.automationDetails.Withdrawal?.assets ?? []).map(asset => `${asset.network}:${asset.token ?? ''}`)
  )).length
  const lastTriggeredAutomation = automations
    .filter(a => a.lastTriggeredAt)
    .sort((a, b) => new Date(b.lastTriggeredAt ?? 0).getTime() - new Date(a.lastTriggeredAt ?? 0).getTime())[0]

  const autoSwapTargetMethod = autoSwapEnabled
    ? getMethod(autoSwapSettings?.targetNetwork ?? '', autoSwapSettings?.targetToken ?? undefined)
    : undefined
  const autoSwapTargetLabel = autoSwapTargetMethod
    ? `${autoSwapTargetMethod.ticker}${autoSwapTargetMethod.hostChain ? ` on ${autoSwapTargetMethod.hostChain.name}` : ''}`
    : 'Unknown target'

  const hasRules = totalRules > 0

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader
          title='Automations'
          subTitle='Move funds automatically. Withdraw to your accounts on a schedule, or convert every incoming payment.' />
        <PageActionRow>
          <Gate permission='WriteAutomation'>
            <PrimaryButton style={{ gap: 8 }} onClick={() => setIsOpen(true)}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>+</span>
              <span>Create automation</span>
            </PrimaryButton>
          </Gate>
        </PageActionRow>
      </PageHeaderRow>

      <Surface>
        <SurfaceHeader>
          <SurfaceTitle>Overview</SurfaceTitle>
          <InlinePill $tone={hasRules ? 'green' : 'neutral'}>
            {hasRules ? 'Active' : 'No rules'}
          </InlinePill>
        </SurfaceHeader>
        <SummaryStrip>
          <SummaryStripItem>
            <SummaryLabel>Configured rules</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : totalRules}
            </SummaryValue>
            <SummaryMeta>
              {withdrawalAutomationCount} withdrawal rule{withdrawalAutomationCount === 1 ? '' : 's'}
              {autoSwapEnabled ? ' and 1 auto-convert rule' : ''}
            </SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Your accounts used</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : destinationAccountCount}
            </SummaryValue>
            <SummaryMeta>Accounts your rules send to</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Watched assets</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : watchedAssetCount}
            </SummaryValue>
            <SummaryMeta>Assets included in rules</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Last run</SummaryLabel>
            <SummaryValue style={{ fontSize: 18 }}>
              {isLoading
                ? <LoadingBar style={{ height: 24, width: 92 }} />
                : lastTriggeredAutomation
                  ? formatDistanceToNow(new Date(lastTriggeredAutomation.lastTriggeredAt ?? 0), { addSuffix: true })
                  : 'Not run yet'}
            </SummaryValue>
            <SummaryMeta>Most recent automation trigger</SummaryMeta>
          </SummaryStripItem>
        </SummaryStrip>
      </Surface>

      <Surface>
        <SurfaceHeader>
          <SurfaceTitle>Rules</SurfaceTitle>
          {hasRules && !isLoading && (
            <InlinePill $tone='neutral'>{totalRules} active</InlinePill>
          )}
        </SurfaceHeader>

        {isLoading && (
          <RuleList>
            {Array.from(Array(3)).map((_, i) => (
              <LoadingBar key={i} style={{ borderRadius: 14, height: 118, width: '100%' }} />
            ))}
          </RuleList>
        )}

        {!isLoading && !hasRules && (
          <EmptyState>
            {canWriteAutomation
              ? 'No automations yet. Create a rule to move funds to one of your accounts automatically, ' +
                'or to convert incoming payments to the currency you choose.'
              : 'No automations yet. You do not have permission to create them.'}
          </EmptyState>
        )}

        {!isLoading && hasRules && (
          <RuleList>
            {autoSwapEnabled && (
              <RuleCard>
                <RuleTopRow>
                  <RuleIdentity>
                    <RuleIcon>
                      <SwapTransactionIcon style={{ width: 18, height: 18 }} />
                    </RuleIcon>
                    <RuleNameStack>
                      <RuleName>Auto-convert on payment</RuleName>
                      <RuleSubMeta>Global configuration</RuleSubMeta>
                    </RuleNameStack>
                  </RuleIdentity>
                  <RuleTopMeta>
                    <InlinePill $tone='blue'>Auto-convert</InlinePill>
                    <InlinePill $tone='green'>Active</InlinePill>
                    <Gate permission='WriteAutomation'>
                      <ActionsMenu
                        title='Disable auto-convert'
                        description='Are you sure you want to disable auto-convert on payment?'
                        action='Disable'
                        actionLabel='Disable auto-convert'
                        onConfirm={async () => {
                          await Radom.updateAutoSwapOnPayment({
                            enabled: false,
                            targetNetwork: autoSwapSettings?.targetNetwork,
                            targetToken: autoSwapSettings?.targetToken
                          })
                            .then(async () => listAutomations())
                            .then(() => successToast('Auto-convert disabled'))
                        }}
                      />
                    </Gate>
                  </RuleTopMeta>
                </RuleTopRow>

                <RuleFlowRow segments={[
                  <RuleSegment key='assets' label='Assets'>
                    <FlowValueText>All supported assets</FlowValueText>
                  </RuleSegment>,
                  <RuleSegment key='trigger' label='Trigger'>
                    <FlowValueText>Every incoming payment</FlowValueText>
                  </RuleSegment>,
                  <RuleSegment key='target' label='Converts to'>
                    {autoSwapTargetMethod?.logo && (
                      <img
                        src={autoSwapTargetMethod.logo}
                        alt={autoSwapTargetLabel}
                        style={{ width: 18, height: 18, borderRadius: '50%' }} />
                    )}
                    <FlowValueText>{autoSwapTargetLabel}</FlowValueText>
                  </RuleSegment>
                ]} />
              </RuleCard>
            )}

            {automations.map(automation => {
              const destination = canReadWithdrawalAccounts
                ? resolveDestination(automation, withdrawalAccounts)
                : undefined
              const assetMethods = getAutomationAssetMethods(automation)
              const createdMeta = automation.createdAt
                ? `Created ${formatDistanceToNow(new Date(automation.createdAt as any), { addSuffix: true })}`
                : cutString(automation.id, 8)

              return <RuleCard key={automation.id}>
                <RuleTopRow>
                  <RuleIdentity>
                    <RuleIcon>
                      <WithdrawalTransactionIcon style={{ width: 18, height: 18 }} />
                    </RuleIcon>
                    <RuleNameStack>
                      <RuleName>{automation.name}</RuleName>
                      <RuleSubMeta>{createdMeta}</RuleSubMeta>
                    </RuleNameStack>
                  </RuleIdentity>
                  <RuleTopMeta>
                    <InlinePill $tone='blue'>{startCase(automation.automationType)}</InlinePill>
                    {automation.lastTriggeredAt
                      ? <InlinePill $tone='green'>
                        Ran {formatDistanceToNow(new Date(automation.lastTriggeredAt), { addSuffix: true })}
                      </InlinePill>
                      : <InlinePill $tone='neutral'>Never run</InlinePill>}
                    <Gate permission='WriteAutomation'>
                      <ActionsMenu
                        title='Delete automation'
                        description='Are you sure you want to delete this automation?'
                        action='Delete automation'
                        actionLabel='Delete automation'
                        onConfirm={async () => {
                          await Radom.deleteAutomation(automation.id)
                            .then(async () => listAutomations())
                            .then(() => successToast('Automation deleted'))
                        }}
                      />
                    </Gate>
                  </RuleTopMeta>
                </RuleTopRow>

                <RuleFlowRow segments={[
                  <RuleSegment key='assets' label='Assets'>
                    <AssetIcons methods={assetMethods} />
                    <FlowValueText>{getAutomationAssetsLabel(automation)}</FlowValueText>
                  </RuleSegment>,
                  <RuleSegment key='trigger' label='Trigger'>
                    <FlowValueText>{getAutomationTriggerLabel(automation)}</FlowValueText>
                  </RuleSegment>,
                  ...(destination
                    ? [<RuleSegment key='destination' label='Destination'>
                      {destination.logo && (
                        <img
                          src={destination.logo}
                          alt={destination.label}
                          style={{ width: 18, height: 18, borderRadius: '50%' }} />
                      )}
                      <FlowValueText>{destination.label}</FlowValueText>
                      {destination.sub && <FlowSub>{destination.sub}</FlowSub>}
                    </RuleSegment>]
                    : [])
                ]} />
              </RuleCard>
            })}
          </RuleList>
        )}
      </Surface>

      {
        isOpen &&
      <AutomationModal
        onAutomationCreated={() => listAutomations()}
        visible={isOpen}
        onClose={() => setIsOpen(false)} />
      }
    </DashboardPage>
  </Container>
})

export default AutomationView
